import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Download, Trash2, AlertTriangle, Loader2 } from 'lucide-react';

const API = '/api';

const formatDateIST = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) + ' IST';
  } catch {
    return new Date(dateStr).toLocaleString();
  }
};

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);
  const [auditStats, setAuditStats] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const loadLogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/activity-logs?page=${logsPage}&limit=20`, { withCredentials: true });
      setLogs(res.data.logs || []);
      setLogsTotalPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  }, [logsPage]);

  const loadAuditStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/audit-stats`, { withCredentials: true });
      setAuditStats(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadLogs(); }, [loadLogs]);
  useEffect(() => { loadAuditStats(); }, [loadAuditStats]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/admin/audit-download`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_trail_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Audit trail downloaded');
      loadAuditStats();
    } catch {
      toast.error('Failed to download audit trail');
    } finally {
      setDownloading(false);
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm('This will permanently delete audit records older than 30 days. Make sure you have downloaded them first. Continue?')) return;
    setCleaning(true);
    try {
      const res = await axios.post(`${API}/admin/audit-cleanup`, {}, { withCredentials: true });
      toast.success(`Cleaned up ${res.data.deleted_count} old records`);
      loadLogs();
      loadAuditStats();
    } catch {
      toast.error('Failed to cleanup audit records');
    } finally {
      setCleaning(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Activity Logs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-12 bg-muted rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="activity-logs-tab">
      {/* Monthly Download Prompt */}
      {auditStats?.cleanup_recommended && (
        <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/20" data-testid="audit-cleanup-banner">
          <CardContent className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 py-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-amber-800 dark:text-amber-200">
                  {auditStats.older_than_30_days.toLocaleString()} audit records older than 30 days
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-0.5">
                  Download the audit trail and clear old records to free up space.
                  Total records: {auditStats.total_records.toLocaleString()}
                </p>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button
                data-testid="audit-download-btn"
                variant="outline"
                size="sm"
                onClick={handleDownload}
                disabled={downloading}
                className="gap-1 border-amber-400 text-amber-800 hover:bg-amber-100"
              >
                {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Download CSV
              </Button>
              <Button
                data-testid="audit-cleanup-btn"
                variant="outline"
                size="sm"
                onClick={handleCleanup}
                disabled={cleaning}
                className="gap-1 border-red-300 text-red-700 hover:bg-red-50"
              >
                {cleaning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Clear Old
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Activity Logs</CardTitle>
            <CardDescription>
              All user activities (timestamps in IST)
              {auditStats && <span className="ml-2">— {auditStats.total_records.toLocaleString()} total records</span>}
            </CardDescription>
          </div>
          <Button
            data-testid="audit-download-all-btn"
            variant="outline"
            size="sm"
            onClick={handleDownload}
            disabled={downloading}
            className="gap-1"
          >
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export CSV
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Resource</TableHead>
                <TableHead>Timestamp (IST)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log, idx) => (
                <TableRow key={idx} data-testid={`activity-log-row-${idx}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{log.user_name}</p>
                      <p className="text-xs text-muted-foreground">{log.user_email}</p>
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="outline">{log.action}</Badge></TableCell>
                  <TableCell>
                    <span className="text-sm">{log.resource_type}</span>
                    {log.resource_id && (
                      <span className="text-xs text-muted-foreground block font-mono">{log.resource_id}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{formatDateIST(log.created_at)}</TableCell>
                </TableRow>
              ))}
              {logs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    No activity logs found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">Page {logsPage} of {logsTotalPages}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setLogsPage(p => Math.max(1, p - 1))} disabled={logsPage === 1}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setLogsPage(p => Math.min(logsTotalPages, p + 1))} disabled={logsPage === logsTotalPages}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivityLogs;
